'use strict';

/*
    SpendingAnalysisChart
    Display an interactive bar chart of monthly transaction.
    Chart library is configurable / modular.
    Parameters:
      parentPage
      axisXOffset
    Methods (see methods for additional usage):
      showChart()
*/

function SpendingAnalysisChart() {
  var _this4 = this;

  this.extends = [_ComponentDataBind, _ResponsiveLayout, _AnalyticsData];
  this.declaredClass = null;
  this.node = null;
  this.id = null;
  this._analytics = new Analytics();

  // user-specified properties
  this.parentPage = null;
  this.axisXOffset = 30; // seems to have no impact.

  // constants
  this._rollingAverageMonthCount = 3;
  this._columnWidth = 4;
  this._columnSpacing = 25;
  this._axisYMultiplier = 1.2;
  this._maxCachedDataAge = 1000 * 60 * 30;
  this._dragPixelSensitivity = 0; // pixel threadshold to be considered a drag event or not
  this._classNames = {
    barHighlightMonth: 'highlight',
    labelHighlightMonth: 'highlight',
    labelRollingMonthAverage: 'three-month-average'
  };

  // charting libraries
  this._chartLibraries = {
    Chartist: {
      initLibrary: function (callback) {
        if (typeof Chartist === 'undefined') {
          $.when($.ajax({
            url: '/member/assets/javascripts/chartist.js',
            dataType: 'script',
            cache: true
          })).always(callback.bind(this));
        } else {
          // why does callback.bind(this) not work?
          // repeating the callback code here
          this.node.style.display = 'block';
          this._isChartLibraryReady = true;
          this.showChart();
        }
      }.bind(this),

      createChart: function () {
        var _this = this;

        this._chartistData = this._getChartData();

        // set width of chart node so that bar spacing is consistent
        this._chartWidth = this._columnSpacing + this._chartistData.points.length * (this._columnSpacing + this._columnWidth);
        this._chartNode.style.width = this._chartWidth + 'px';

        // chart properties
        var data = {
          labels: this._chartistData.points.map(function (a) {
            return a.label;
          }),
          series: [this._chartistData.points.map(function (a) {
            return a.value;
          })]
        };
        var axisX = {
          position: 'start',
          offset: this.axisXOffset,
          scaleMinSpace: this._columnSpacing,
          showGrid: false
        };
        var axisY = {
          offset: 0,
          showGrid: false,
          showLabel: false
        };
        var options = {
          axisX: axisX,
          axisY: axisY,
          classNames: {},
          height: 'calc(100% - 40px)',
          high: this._chartistData.yAxisMax,
          width: '100%'
        };

        if (this._chartNode && this._isMouseoverChart === false) {
          var chart = new Chartist.Bar('#' + this._chartNode.id, data, options);

          // post-draw modifications
          chart.on('created', function () {
            _this._chartLibrary.onChartComplete();
          });
        }
      }.bind(this),

      onChartComplete: function () {
        this._onChartComplete();
      }.bind(this),

      getCssQueryForBarContainer: function getCssQueryForBarContainer(className) {
        return '.ct-series-a';
      },
      getCssQueryForBarByClass: function getCssQueryForBarByClass(className) {
        return '.ct-series-a .ct-bar.' + className;
      },
      getCssQueryForLabelByClass: function getCssQueryForLabelByClass(className) {
        return '.ct-labels .ct-label.' + className;
      },
      getCssQueryForBarByIndex: function getCssQueryForBarByIndex(barIndex) {
        return '.ct-series-a .ct-bar:nth-of-type(' + barIndex + ')';
      },
      getCssQueryForLabelByIndex: function getCssQueryForLabelByIndex(labelIndex) {
        return '.ct-labels foreignObject:nth-of-type(' + labelIndex + ') .ct-label';
      }
    }
  };
  this._chartLibrary = this._chartLibraries.Chartist;

  // element references
  this._chartNode = null;
  this._spinnerNode = null;
  this._dragableArea = null;

  // data caching
  this._monthlyData = [];
  this._dataError = false;
  this._highlightedMonthIndex = 0;
  this._highlightedMonthName = '';
  this._highlightedMonthAmount = 0;
  this._averageMonthlyAmount = null;
  this._monthCountForAverage = 0;
  this._averageDailyAmount = null;
  this._axisYScope = 0;
  this._axisYScopeInitial = 0;
  this._chartWidth = 0;
  this._visibleColumnCount = 0;

  // actions
  this._spendingAnalysisChartDataAction = 'cards.body[0].get_spending_analysis';

  // internal flags
  this._isChartLibraryReady = false;
  this._isChartReady = false;
  this._isMouseoverChart = false;
  this._yearLineContainer = null;
  this._monthLabelContainer = null;

  this.init = function () {
    var _this2 = this;

    this._super();
    this.node.style.display = 'block';
    this.node.style.right = 0;
    this._visibleColumnCount = Math.floor((this.node.offsetParent ? this.node.offsetWidth : 470) / (this._columnSpacing + this._columnWidth));

    // Add loading spinner
    this._spinnerNode = document.createElement('span');
    this._spinnerNode.className = 'progress-spinner progress-spinner-vertical-center progress-spinner--light';
    this.node.appendChild(this._spinnerNode);

    // Add chart node
    this._chartNode = document.createElement('div');
    this._chartNode.id = 'spending-analysis-chart';
    this.node.appendChild(this._chartNode);

    this._dragableArea = document.querySelector('.js--draggable-area');

    // Add error message
    this._errorNode = document.createElement('div');
    this._errorNode.className = 'spending-analysis-chart-error';
    this.node.appendChild(this._errorNode);
    this._errorNode.appendChild(document.createTextNode('Spending Analysis data temporarily unavailable'));

    this._addEventHandlers();
    this._showSpinner();

    // make sure a 'SpendingAnalysisChart' model exists
    if (typeof this.presenter.models.SpendingAnalysisChart === 'undefined') {
      this.presenter.createModel('SpendingAnalysisChart');
      this._updateExportedValues();
    }

    // load charting scripts; show chart once done
    this._chartLibrary.initLibrary(function () {
      this.node.style.display = 'block';
      this._isChartLibraryReady = true;
      this.showChart();
    });

    // if there is previously cached data and it's not too stale, display that temporarily
    if (typeof Storage !== 'undefined') {
      var cachedData = this.presenter.getModelProperty('SpendingAnalysisChart._rawData');
      var cachedDataDate = this.presenter.getModelProperty('SpendingAnalysisChart._rawDataDate');
      if (cachedData && cachedDataDate && new Date().getTime() - cachedDataDate < this._maxCachedDataAge) {
        this._onGetMonthlySpendingData(cachedData, 200, true);
        this.showChart();
      } else {
        // load chart data
        this.presenter.performAction(this._spendingAnalysisChartDataAction, null, function (data, response) {
          _this2._onGetMonthlySpendingData(data, response);
          _this2.showChart();
        });
      }
    }
  };

  this.initAllReady = function () {};

  this.destroy = function () {
    this._super();

    // remove callback on action
    if (this._getSpendingAnalysisChartDataCallback) {
      this.presenter.removeCallbackFromAction(this._spendingAnalysisChartDataAction, this._getSpendingAnalysisChartDataCallback);
    }

    this._clearExportData();
  };

  /*
      Show the chart assuming we have appropriate data.
  */
  this.showChart = function () {
    if (this._monthlyData && this._monthlyData.length && !this._dataError && this._isChartLibraryReady && document.getElementById(this._chartNode.id)) {
      this._hideSpinner();
      this._hideError();
      this._chartNode.style.display = 'block';
      this.registerResponsiveFunction(this._calcInnerHeight.bind(this), null, mogo.registry.mogoUI.config.mediaQueries.small);
      this._chartLibrary.createChart();
    } else {
      this._showError();
    }
  };

  this._calcInnerHeight = function () {
    // Set CSS variable to be used to size chart area on mobile
    // CSS VH doesnt work well with iOS safari address bar show/hide
    var vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', vh + 'px');
  };

  this._addEventHandlers = function () {
    var touchStartXPosition = null;
    var dragStartRightValue = 0;
    var isDragEvent = false;

    // extra stuff needed for touch events
    var ongoingTouches = [];
    function copyTouch(touch) {
      return { identifier: touch.identifier, pageX: touch.pageX, pageY: touch.pageY };
    }
    function ongoingTouchIndexById(idToFind) {
      for (var i = 0; i < ongoingTouches.length; i++) {
        var id = ongoingTouches[i].identifier;
        if (id === idToFind) {
          return i;
        }
      }
      return -1;
    }
    function getEventXPosition(event, type) {
      var x = type == 'page' ? event.pageX : event.offsetX;

      if (type === 'offset') {
        var parentEl = event.srcElement.offsetParent;
        if (parentEl && parentEl.x) {
          var correctX = parentEl.x.baseVal.value;
          x += correctX;
        }
      }

      // only when using devtools?
      if (event.changedTouches) {
        if (event.layerX) {
          x = event.layerX;
        } else if (event.changedTouches[0].pageX) {
          if (type == 'page') {
            x = event.changedTouches[0].pageX;
          } else {
            var left = 0;
            var elem = this._chartNode;
            while (elem) {
              left += parseInt(elem.offsetLeft);
              elem = elem.offsetParent;
            }
            x = event.changedTouches[0].pageX - left;
          }
        }
      }
      return x;
    }
    function clearAnySelectedText() {
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      } else if (document.selection) {
        document.selection.empty();
      }
    }

    // on mouse down or touch start, allow chart to be draggable
    var startDragChart = function (event) {
      // touch events - add related touch event to list of ongoing ones
      if (event.changedTouches) {
        ongoingTouches.push(copyTouch(event.changedTouches[0]));
      }

      // store mouse position and current chart offset
      touchStartXPosition = getEventXPosition(event, 'page');
      dragStartRightValue = parseInt(this._chartNode.style.right) | 0;
    }.bind(this);
    this._dragableArea.addEventListener('mousedown', startDragChart);
    this._dragableArea.addEventListener('touchstart', startDragChart);

    // on mouse move or touch move, drag chart
    var dragChart = function (event) {
      // only drag if preceeded by mouse down event
      if (touchStartXPosition) {
        var newXPosition = getEventXPosition(event, 'page');
        var newRight = dragStartRightValue + (touchStartXPosition - newXPosition);

        // new right position is valid and falls within the bounds of the first and last bars
        if (!isNaN(newRight) && newRight < 0 && newRight > -(this._columnSpacing + this._columnWidth) * (this._monthlyData.length - this._visibleColumnCount - 1)) {
          this._chartNode.style.right = newRight + 'px';
        }

        // set drag event flag if position is greater than a small amount
        if (Math.abs(newRight - dragStartRightValue) > this._dragPixelSensitivity) {
          isDragEvent = true;
        }
      }

      // don't highlight content when dragging
      event.stopPropagation();
      event.preventDefault();
    }.bind(this);
    this._dragableArea.addEventListener('mousemove', dragChart);
    this._dragableArea.addEventListener('touchmove', dragChart);

    // on mouse up or touch end, end chart drag
    var endDragChart = function (event) {
      var _this3 = this;

      clearAnySelectedText();
      // touch events - remove related touch event from list of ongoing ones
      if (event.changedTouches) {
        var idx = ongoingTouchIndexById(event.changedTouches[0].identifier);
        ongoingTouches.splice(idx, 1);
      }

      // if not part of a drag event, treat as a click event (mouseup)
      if (!isDragEvent && event.type !== 'mouseleave' && event.type !== 'touchend') {
        var farRightMax = document.getElementsByClassName('ct-labels')[0].getBoundingClientRect().width;
        var lastFillerX = (this._columnSpacing + this._columnWidth) * this._visibleColumnCount + this._columnSpacing; // prevent clicking on the filler items
        var clickXPosition = getEventXPosition(event, 'offset');
        if (clickXPosition >= lastFillerX && clickXPosition <= farRightMax) {
          this._chartNode.style.right = clickXPosition - this._chartWidth + this._columnSpacing + this._columnWidth + 'px';
        }
      }

      // help 'snap' align months to highlight bar:
      var pageTargets = document.querySelectorAll('.ct-label');
      var highlightBar = document.querySelector('.card-analysis__current-month-highlight');
      pageTargets.forEach(function (item, index) {
        _this3._isColliding(highlightBar, item, index);
      });

      // clear start position value (so subsequent mousemove events don't drag)
      touchStartXPosition = null;

      // reset drag event flag
      isDragEvent = false;
    }.bind(this);
    this._dragableArea.addEventListener('mouseup', endDragChart);
    this._dragableArea.addEventListener('touchend', endDragChart);
    this._dragableArea.addEventListener('mouseleave', endDragChart);
  };

  this._isColliding = function (root, target, targetIndex) {
    var rootBoundingRect = root.getBoundingClientRect();
    var targetBoundingRect = target.getBoundingClientRect();

    // if half of the target overlaps the inside of the root element
    if (targetBoundingRect.right > rootBoundingRect.left + 10 && targetBoundingRect.left < rootBoundingRect.right) {
      // calculate positioning to center target over root
      var widthDiff = rootBoundingRect.width / 2 - targetBoundingRect.width / 2;
      var leftDiff = rootBoundingRect.left - targetBoundingRect.left;
      var positionAdjust = -leftDiff - widthDiff;
      var currentPosition = _this4._chartNode.style.right.replace('px', '');

      var newPosition = currentPosition - 1 + positionAdjust;

      // reposition chart
      _this4._chartNode.style.right = newPosition + 'px';

      // update chart appearance
      _this4._highlightedMonthIndex = targetIndex + 1;
      _this4._onChartUpdate();
    }
  };

  this._onGetMonthlySpendingData = function (data, response, preventCaching) {
    var doHandleError = function () {
      this._dataError = true;
      this._showError();

      // remove chart export data and update external components
      this._clearExportData();
    }.bind(this);

    if (response === 200) {
      this._dataError = false;

      // parsing raw data
      var monthlyData = data.monthly_data.sort(function (a, b) {
        return moment(a.date) - moment(b.date);
      }).map(function (record) {
        return [record.date.substring(0, 10), record.amount];
      });

      // cache raw data outside the component
      if (!preventCaching) {
        this.presenter.setModelProperty('SpendingAnalysisChart._rawData', data);
        this.presenter.setModelProperty('SpendingAnalysisChart._rawDataDate', new Date().getTime());
      }

      // data returned by BE does not include months the user did not have a card
      // add empty data records for any missed months
      if (monthlyData.length) {
        var currentMonth = monthlyData[0][0];
        var currentDataIndex = 0;
        var lastMonth = monthlyData[monthlyData.length - 1][0];
        while (currentMonth != lastMonth) {
          console.log(monthlyData[currentDataIndex][0], currentMonth, monthlyData[currentDataIndex][0] != currentMonth);
          if (monthlyData[currentDataIndex][0] != currentMonth) {
            monthlyData.splice(currentDataIndex, 0, [currentMonth, 0]);
          }
          currentMonth = moment(currentMonth).add(1, 'months').format('YYYY-MM-DD');
          currentDataIndex++;
        }
      }

      // backfill monthly data with enough empty months so user can drag last valid month to highlight bar (screen center)
      var backfillData = [];
      // if we have data, use first date in array, otherwise use current date plus 1 month for generating months starting from current month
      this._startDate = monthlyData.length ? monthlyData[0][0] : moment().add(1, 'M').format('YYYY-MM-DD');
      for (var i = 1; i <= this._visibleColumnCount; i++) {
        var month = [moment(this._startDate).subtract(i, 'months').format('YYYY-MM-DD'), null];
        backfillData.unshift(month);
      }

      // extract monthly data to feed to the chart
      if (monthlyData.length) {
        this._monthlyData = backfillData.concat(monthlyData);
      } else {
        this._monthlyData = backfillData;
      }

      // set highlighted month to be the last data point
      // (done here so this value can be used in calculations to determine chart height)
      this._highlightedMonthIndex = this._monthlyData.length;

      // set Spent Today (static content)
      this.presenter.setModelProperty('SpendingAnalysisChart._spentToday', data.spent_today);
    } else {
      doHandleError();
    }
  };

  this._getChartData = function () {
    // clear old values
    var chartPoints = [];

    var countDataValues = this._monthlyData.length;
    for (var i = 0; i < countDataValues; i++) {
      var dataPoint = this._monthlyData[i];
      chartPoints.push({
        date: dataPoint[0],
        label: this.utils.getMonthInitialFromDateString(dataPoint[0]),
        value: dataPoint[1]
      });
    }

    // get and store initial Y axis scope
    this._axisYScopeInitial = this._getAxisYScope(true);

    // return data point values and max height of bars
    return {
      points: chartPoints,
      yAxisMax: this._axisYScopeInitial
    };
  };

  this._getAxisYScope = function (useWholeRange) {
    var highestValue = null;
    var oldestVisibleMonthIndex = this._highlightedMonthIndex - this._visibleColumnCount;
    var newestVisibleMonthIndex = this._highlightedMonthIndex;
    var countDataValues = this._monthlyData.length;
    for (var monthIndex = 0; monthIndex < countDataValues; monthIndex++) {
      var dataPoint = this._monthlyData[monthIndex];
      var isMonthVisible = monthIndex >= oldestVisibleMonthIndex && monthIndex <= newestVisibleMonthIndex;
      if ((useWholeRange || isMonthVisible) && dataPoint[1] > highestValue) {
        highestValue = dataPoint[1];
      }
    }
    return highestValue * this._axisYMultiplier;
  };

  this._onChartComplete = function () {
    this._isChartReady = true;
    this._onChartUpdate();
    this._updateYearLines();
    this._monthLabelContainer = document.getElementsByClassName('ct-labels')[0];
  };

  this._onChartUpdate = function () {
    var isMinimalDisplay = this.getActiveViewport() === 'small';

    // set last month as highlighted and update 3-month rolling average
    this._setHighlightMonth();
    this._setRollingAverageMonths();

    // rescale the graph
    this._axisYScope = this._getAxisYScope(false);
    var barsContainer = this._chartNode.querySelector(this._chartLibrary.getCssQueryForBarContainer());
    var scaleRatio = this._axisYScopeInitial / this._axisYScope;
    barsContainer.style.transformOrigin = '50% calc(100% - 5px)';
    barsContainer.style.transform = 'scaleY(' + scaleRatio + ')';

    // export values after they have been determined
    this._updateExportedValues();

    // tell page that the chart has updated
    this.parentPage.onUpdateChart(isMinimalDisplay);
  };

  this._updateYearLines = function () {
    if (!this._yearLineContainer) {
      this._yearLineContainer = document.createElement('div');
      this._yearLineContainer.addClass('card-analysis__year-container');
      this._chartNode.appendChild(this._yearLineContainer);

      // draw year lines
      if (this._chartistData.points && this._chartistData.points.length) {
        var year = null;
        var lineElement = null;
        for (var i = 0; i < this._chartistData.points.length; i++) {
          if (year != moment(this._chartistData.points[i].date).year()) {
            year = moment(this._chartistData.points[i].date).year();
            // create year label
            var yearLabel = document.createElement('span');
            yearLabel.innerText = year;
            yearLabel.addClass('card-analysis__year-label');
            lineElement = document.createElement('div');
            lineElement.addClass('card-analysis__year-line');
            lineElement.style.width = this._columnWidth + 'px';
            lineElement.appendChild(yearLabel);
            this._yearLineContainer.appendChild(lineElement);
          } else {
            lineElement.style.width = lineElement.offsetWidth + this._columnSpacing + this._columnWidth + 'px';
          }
        }
      }
    }
  };

  this._setHighlightMonth = function () {
    var highlightedDataValue = this._monthlyData[this._highlightedMonthIndex - 1];
    this._highlightedMonthName = this._highlightedMonthIndex != this._monthlyData.length ? this.utils.getMonthNameFromDateString(highlightedDataValue[0]) : '';
    this._highlightedMonthAmount = highlightedDataValue[1];
    this._averageDailyAmount = this.utils.getAverageDailyAmount(highlightedDataValue[0]);

    // remove any previously highlighted months
    var currentBar = this._chartNode.querySelector(this._chartLibrary.getCssQueryForBarByClass(this._classNames.barHighlightMonth));
    var currentLabel = this._chartNode.querySelector(this._chartLibrary.getCssQueryForLabelByClass(this._classNames.labelHighlightMonth));
    if (currentBar) {
      currentBar.classList.remove(this._classNames.barHighlightMonth);
    }
    if (currentLabel) {
      currentLabel.classList.remove(this._classNames.labelHighlightMonth);
    }

    // highlight current month bar and label
    var barIndex = this.utils.getBarIndexFromLabelIndex(this._highlightedMonthIndex);
    var newCurrentBar = this._chartNode.querySelector(this._chartLibrary.getCssQueryForBarByIndex(barIndex));
    var newCurrentLabel = this._chartNode.querySelector(this._chartLibrary.getCssQueryForLabelByIndex(this._highlightedMonthIndex));
    if (newCurrentBar) {
      newCurrentBar.classList.add(this._classNames.barHighlightMonth);
    }
    if (newCurrentLabel) {
      newCurrentLabel.classList.add(this._classNames.labelHighlightMonth);
    }
  };

  this._setRollingAverageMonths = function () {
    var nthLastValidMonthIndex = this._visibleColumnCount + this._rollingAverageMonthCount;
    this._monthCountForAverage = this._highlightedMonthIndex > nthLastValidMonthIndex ? this._rollingAverageMonthCount : this._highlightedMonthIndex === nthLastValidMonthIndex ? this._rollingAverageMonthCount - 1 : '0';
    this._averageMonthlyAmount = this.utils.getAverageMonthlyAmount(this._monthlyData.map(function (a) {
      return a[1];
    }), this._highlightedMonthIndex - 1, this._monthCountForAverage);

    // remove any previously highlighted months
    var rollingAverageLabels = this._chartNode.querySelectorAll(this._chartLibrary.getCssQueryForLabelByClass(this._classNames.labelRollingMonthAverage));
    for (var i = 0; i < rollingAverageLabels.length; i++) {
      rollingAverageLabels[i].classList.remove(this._classNames.labelRollingMonthAverage);
    }

    // highlight up to n months previous to current month
    for (i = this._highlightedMonthIndex; i > this._visibleColumnCount && i >= this._highlightedMonthIndex - this._rollingAverageMonthCount; i--) {
      var previousMonthLabel = this._chartNode.querySelector(this._chartLibrary.getCssQueryForLabelByIndex(i));
      previousMonthLabel.classList.add(this._classNames.labelRollingMonthAverage);
    }
  };

  this._updateExportedValues = function () {
    this.presenter.setModelProperty('SpendingAnalysisChart._highlightedMonthIndex', this._currentMonthName);
    this.presenter.setModelProperty('SpendingAnalysisChart._highlightedMonthName', this._highlightedMonthName);
    this.presenter.setModelProperty('SpendingAnalysisChart._highlightedMonthAmount', this._highlightedMonthAmount);
    this.presenter.setModelProperty('SpendingAnalysisChart._monthCountForAverage', this._monthCountForAverage);
    this.presenter.setModelProperty('SpendingAnalysisChart._averageMonthlyAmount', this._averageMonthlyAmount);
    this.presenter.setModelProperty('SpendingAnalysisChart._averageDailyAmount', this._averageDailyAmount);
    this.presenter.setModelProperty('SpendingAnalysisChart._axisYScope', this._axisYScope);
    this.presenter.updateDOM();
  };

  this._clearExportData = function () {
    this.presenter.setModelProperty('SpendingAnalysisChart._highlightedMonthIndex', null);
    this.presenter.setModelProperty('SpendingAnalysisChart._highlightedMonthName', null);
    this.presenter.setModelProperty('SpendingAnalysisChart._highlightedMonthAmount', null);
    this.presenter.setModelProperty('SpendingAnalysisChart._averageMonthlyAmount', null);
    this.presenter.setModelProperty('SpendingAnalysisChart._monthCountForAverage', null);
    this.presenter.setModelProperty('SpendingAnalysisChart._averageDailyAmount', null);
    this.presenter.setModelProperty('SpendingAnalysisChart._axisYScope', null);
  };

  this._showSpinner = function () {
    this._chartNode.style.display = 'none';
    this._errorNode.style.display = 'none';
    this._spinnerNode.style.display = 'block';
  };

  this._hideSpinner = function () {
    this._spinnerNode.style.display = 'none';
  };

  this._showError = function () {
    this._chartNode.style.display = 'none';
    this._errorNode.style.display = 'block';
    this._spinnerNode.style.display = 'none';
  };

  this._hideError = function () {
    this._errorNode.style.display = 'none';
  };

  this.utils = {
    getMomentObjectFromDateString: function getMomentObjectFromDateString(dateString) {
      return moment(dateString, 'YYYY-MM-DD');
    },


    getMonthNameFromDateString: function (dateString) {
      return this.utils.getMomentObjectFromDateString(dateString).format('MMMM');
    }.bind(this),

    getMonthInitialFromDateString: function (dateString) {
      return this.utils.getMonthNameFromDateString(dateString).substr(0, 1);
    }.bind(this),

    getAverageMonthlyAmount: function getAverageMonthlyAmount(monthlyAmountList, lastMonthIndex, monthCount) {
      var averageValue = null;

      if (monthlyAmountList.filter(function (item) {
        return item !== null;
      }).length >= 2) {
        var sliceStart = lastMonthIndex - monthCount;
        var arrayToAverage = monthlyAmountList.slice(sliceStart, lastMonthIndex);
        if (arrayToAverage.length > 1) {
          averageValue = arrayToAverage.reduce(function (p, c) {
            return p + c;
          }) / arrayToAverage.length;
        }
      }
      return averageValue;
    },


    // once we get daily data, change calculation to check for 7 days with
    // transactions in a month
    getAverageDailyAmount: function (dateString) {
      var averageValue = null;
      var monthData = this._monthlyData.find(function (element) {
        return element[0] === dateString;
      });
      if (monthData && typeof monthData[1] === 'number') {
        var monthSpend = monthData[1];
        var now = moment();
        var monthStart = this.utils.getMomentObjectFromDateString(dateString);
        if (moment().isSame(dateString, 'month')) {
          averageValue = this.presenter.getModelProperty('SpendingAnalysisChart._rawData.avg_daily_spend'); // current month (BE determined)
        } else {
          averageValue = monthSpend / monthStart.daysInMonth(); // previous month
        }
      }
      return averageValue;
    }.bind(this),

    getBarIndexFromLabelIndex: function (labelIndex) {
      return labelIndex - this._visibleColumnCount;
    }.bind(this)
  };
}